
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supportService } from '../services/api';
import { AlertCircle, CheckCircle, Upload, X } from 'lucide-react';

const SupportPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [image, setImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                setError(t('support.fileTooLarge', 'File size must be less than 5MB'));
                return;
            }
            setImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!subject.trim() || !message.trim()) {
            setError(t('support.requiredFields', 'Subject and Message are required'));
            return;
        }

        setIsSubmitting(true);

        try {
            const formData = new FormData();
            formData.append('subject', subject);
            formData.append('message', message);
            if (image) {
                formData.append('image', image);
            }

            await supportService.createTicket(formData);
            setSuccess(t('support.successMessage', 'Your ticket has been submitted successfully.'));
            setSubject('');
            setMessage('');
            removeImage();
            // Optional: redirect after delay
        } catch (err: any) {
            console.error('Failed to submit ticket', err);
            setError(err.response?.data?.message || t('support.errorMessage', 'Failed to submit ticket. Please try again.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-2xl">
            <h1 className="text-3xl font-bold mb-6">{t('support.title', 'Contact Support')}</h1>

            <div className="bg-white rounded-lg shadow-md p-6">
                {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 flex items-center">
                        <AlertCircle className="text-red-500 mr-2" size={20} />
                        <span className="text-red-700">{error}</span>
                    </div>
                )}

                {success && (
                    <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 flex items-center">
                        <CheckCircle className="text-green-500 mr-2" size={20} />
                        <span className="text-green-700">{success}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('support.subject', 'Subject')} *
                        </label>
                        <input
                            type="text"
                            id="subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500"
                            placeholder={t('support.subjectPlaceholder', 'Briefly describe your issue')}
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('support.message', 'Message')} *
                        </label>
                        <textarea
                            id="message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={6}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500"
                            placeholder={t('support.messagePlaceholder', 'Provide details about your problem...')}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('support.attachment', 'Attachment (Optional)')}
                        </label>

                        {!imagePreview ? (
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors"
                            >
                                <Upload className="text-gray-400 mb-2" size={32} />
                                <span className="text-sm text-gray-500">{t('support.clickToUpload', 'Click to upload screenshot')}</span>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleImageChange}
                                    className="hidden"
                                    accept="image/*"
                                />
                            </div>
                        ) : (
                            <div className="relative inline-block mt-2">
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="max-h-48 rounded-lg border border-gray-200"
                                />
                                <button
                                    type="button"
                                    onClick={removeImage}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={() => navigate('/dashboard')}
                            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                        >
                            {t('support.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    {t('support.sending')}
                                </>
                            ) : (
                                t('support.submit')
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SupportPage;
